import { mutationOptions, queryOptions } from "@tanstack/react-query";
import { z } from "zod";
import { api } from "./apiClient";
import { FORMS_API_BASE_ENDPOINT } from "./constants";
import { invalidateAll } from "./invalidation";
import { hasErrorCode } from "./errors";
import { forgetInviteSessionCsrfToken, inviteSessionApi } from "./assistantInviteSessionClient";

const BASE_ENDPOINT = `${FORMS_API_BASE_ENDPOINT}/assistant-invitations`;

const keys = {
  all: () => ["assistantInvitations"] as const,
  mine: () => [...keys.all(), "mine"] as const,
  invitation: (invitationId: string) => [...keys.all(), "invitation", invitationId] as const,
} as const;

const AssistantInvitationStatusSchema = z.enum(["waiting", "active", "submitted", "expired", "revoked"]);
export type AssistantInvitationStatus = z.infer<typeof AssistantInvitationStatusSchema>;

const AssistantInvitationSchema = z.object({
  invitationId: z.string(),
  email: z.string(),
  reason: z.string(),
  createdAt: z.string(),
  expiresAt: z.string(),
  status: AssistantInvitationStatusSchema,
  submittedRecordId: z.string().nullish(),
  submittedAt: z.string().nullish(),
});
export type AssistantInvitation = z.infer<typeof AssistantInvitationSchema>;

export const assistantInvitationsQueryOptions = () =>
  queryOptions({
    queryKey: keys.mine(),
    queryFn: () => api.get(BASE_ENDPOINT, { schema: z.array(AssistantInvitationSchema) }),
    throwOnError: true,
  });

export const CREATE_ASSISTANT_INVITATION_ERROR_CODES = {
  invalidEmail: "INVALID_EMAIL",
  invalidReason: "INVALID_REASON",
  invalidExpiry: "INVALID_EXPIRY",
  emailNotSent: "EMAIL_NOT_SENT",
} as const;

export const createAssistantInvitationMutationOptions = () =>
  mutationOptions({
    mutationFn: (payload: { email: string; reason: string; expiresInHours: number }) =>
      api.post(BASE_ENDPOINT, payload, { schema: AssistantInvitationSchema }),
    onSuccess: () => invalidateAll(),
  });

export const revokeAssistantInvitationMutationOptions = () =>
  mutationOptions({
    mutationFn: (invitationId: string) => api.delete(`${BASE_ENDPOINT}/${invitationId}`),
    onSuccess: () => invalidateAll(),
  });

const AssistantInvitationInfoSchema = z.object({
  maskedEmail: z.string(),
  inviterName: z.string(),
  organisationName: z.string(),
  expiresAt: z.string(),
  status: AssistantInvitationStatusSchema,
  language: z.enum(["sv", "en", "no"]),
});
export type AssistantInvitationInfo = z.infer<typeof AssistantInvitationInfoSchema>;

export const assistantInvitationQueryOptions = (invitationId: string) =>
  queryOptions({
    queryKey: keys.invitation(invitationId),
    queryFn: () => inviteSessionApi(invitationId).get("invitation", { schema: AssistantInvitationInfoSchema }),
    throwOnError: true,
  });

export const requestInvitationCodeMutationOptions = (invitationId: string) =>
  mutationOptions({
    mutationFn: () => inviteSessionApi(invitationId).post("otp/request", {}),
  });

export const isInvalidCodeError = (error: unknown) => hasErrorCode(error, "INVALID_CODE");

export const isInvitationClosedError = (error: unknown) => hasErrorCode(error, "INVITATION_CLOSED");

export const verifyInvitationCodeMutationOptions = (invitationId: string) =>
  mutationOptions({
    mutationFn: (payload: { code: string }) => inviteSessionApi(invitationId).post("otp/verify", payload),
    onSuccess: () => {
      forgetInviteSessionCsrfToken(invitationId);
      return invalidateAll();
    },
    onSettled: (_, error) => (isInvitationClosedError(error) ? invalidateAll() : undefined),
  });
